import { INVITE_PARAM, SESSION_STORAGE_KEY } from "@swf/shared";

/**
 * Stage-2 loader. The merchant snippet queues commands on window.swf before
 * this script arrives; we drain that queue, decide whether the main bundle is
 * needed at all, and lazy-import it. Everything heavy lives in the main bundle.
 *
 * M1 scope: config comes from the snippet's init call (no control-plane
 * bootstrap endpoint yet — that lands in M3).
 */

interface SnippetQueue {
  (...args: unknown[]): void;
  q?: unknown[][];
}

export interface WidgetInitConfig {
  siteKey: string;
  /** M1/M2 only: where to load the main bundle from; later pinned via bootstrap. */
  bundleUrl?: string;
  /** Sync server base URL (http/https; upgraded to ws/wss internally). */
  syncUrl?: string;
  /**
   * CSS selector of an element inside the store header where the SWF icon
   * mounts. When absent (or not found), falls back to a floating button.
   */
  headerTarget?: string;
  /**
   * Path prefixes that are private to each shopper (personal bag, account,
   * checkout). On these pages nothing about the location is shared and
   * followers stay where they were.
   */
  privatePaths?: string[];
  accentColor?: string;
}

/** Why the main bundle was loaded — drives whether the topsheet auto-opens. */
export type BootReason = "invite" | "click" | "restore";

export interface BootOptions {
  reason: BootReason;
}

/**
 * Command bus between the loader and the (lazily loaded) app. Merchant code
 * calls e.g. `swf('bag:add')`; commands arriving before the app is up are
 * buffered here and drained when the app registers its handler.
 */
export interface SwfCommandBus {
  pending: unknown[][];
  handler: ((args: unknown[]) => void) | null;
}

declare global {
  interface Window {
    swf?: SnippetQueue;
    __swfLoaded?: boolean;
    __swfBus?: SwfCommandBus;
  }
}

/** Default-state icon (user-plus): fill = currentColor so CSS drives color. */
export const ICON_DEFAULT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true"><path d="M256,136a8,8,0,0,1-8,8H232v16a8,8,0,0,1-16,0V144H200a8,8,0,0,1,0-16h16V112a8,8,0,0,1,16,0v16h16A8,8,0,0,1,256,136Zm-57.87,58.85a8,8,0,0,1-12.26,10.3C165.75,181.19,138.09,168,108,168s-57.75,13.19-77.87,37.15a8,8,0,0,1-12.25-10.3c14.94-17.78,33.52-30.41,54.17-37.17a68,68,0,1,1,71.9,0C164.6,164.44,183.18,177.07,198.13,194.85ZM108,152a52,52,0,1,0-52-52A52.06,52.06,0,0,0,108,152Z"></path></svg>';

function main(): void {
  if (window.__swfLoaded) return;
  window.__swfLoaded = true;

  // Ordering-independent init: with `async` loading, this script can execute
  // before OR after the merchant snippet's swf('init') call. Mount on
  // whichever arrives last — the drained queue or a late direct call.
  let initialized = false;
  let bundleRequested = false;
  let activeCfg: WidgetInitConfig | null = null;
  const bus = (window.__swfBus ??= { pending: [], handler: null });

  const requestBundle = (reason: BootReason): void => {
    if (bundleRequested || !activeCfg) return;
    bundleRequested = true;
    document.getElementById("swf-launcher")?.remove();
    void loadMainBundle(activeCfg, { reason });
  };

  const tryInit = (payload: unknown): void => {
    if (initialized || !payload || typeof payload !== "object") return;
    const cfg = payload as WidgetInitConfig;
    if (!cfg.siteKey) {
      console.warn("[swf] init config is missing siteKey");
      return;
    }
    initialized = true;
    activeCfg = cfg;

    const hasInvite = new URLSearchParams(location.search).has(INVITE_PARAM);
    const hasSession = sessionStorage.getItem(SESSION_STORAGE_KEY) !== null;
    // Cost control: uninvolved shoppers never pay for the main bundle — until
    // they have an invite, an active session, or invoke a command, we only
    // mount a lightweight launcher that pulls the bundle on demand.
    if (hasInvite || hasSession || bus.pending.length > 0) {
      requestBundle(hasInvite ? "invite" : hasSession ? "restore" : "click");
    } else {
      mountDeferredLauncher(cfg, () => requestBundle("click"));
    }
  };

  const handle = (args: unknown[]): void => {
    const [command, payload] = args;
    if (command === "init") {
      tryInit(payload);
    } else if (typeof command === "string") {
      // App command (e.g. "bag:add"): buffer or forward, and make sure the
      // app is actually coming up to act on it.
      if (bus.handler) bus.handler(args);
      else bus.pending.push(args);
      requestBundle("click");
    }
  };

  const queued = window.swf?.q ?? [];
  // Replace the queue shim first so a snippet running after us is handled too.
  window.swf = ((...args: unknown[]) => handle(args)) as SnippetQueue;
  for (const args of queued) handle(args);
}

// Captured at module evaluation: document.currentScript is null by the time
// any async callback runs.
const SCRIPT_BASE = (() => {
  const src = (document.currentScript as HTMLScriptElement | null)?.src;
  return src ? new URL(".", src).href : location.href;
})();

async function loadMainBundle(cfg: WidgetInitConfig, opts: BootOptions): Promise<void> {
  const url = cfg.bundleUrl ?? new URL("widget.js", SCRIPT_BASE).href;
  try {
    const mod = (await import(/* @vite-ignore */ url)) as {
      boot: (cfg: WidgetInitConfig, opts: BootOptions) => void;
    };
    mod.boot(cfg, opts);
  } catch (err) {
    console.error("[swf] failed to load widget bundle", err);
  }
}

/**
 * Minimal pre-bundle launcher, swapped out for the real UI on first click.
 * Renders a black icon inside the merchant's header slot when headerTarget
 * resolves; otherwise a floating pill button.
 */
function mountDeferredLauncher(cfg: WidgetInitConfig, onActivate: () => void): void {
  const slot = cfg.headerTarget ? document.querySelector(cfg.headerTarget) : null;

  const host = document.createElement(slot ? "span" : "div");
  host.id = "swf-launcher";
  const shadow = host.attachShadow({ mode: "open" });

  const btn = document.createElement("button");
  btn.setAttribute("aria-label", "Shop with friends");

  if (slot) {
    btn.innerHTML = ICON_DEFAULT_SVG;
    btn.style.cssText =
      "all:initial;cursor:pointer;display:inline-flex;align-items:center;color:#111;padding:4px;line-height:0;";
    shadow.appendChild(btn);
    slot.appendChild(host);
  } else {
    btn.textContent = "🛍️ Shop with friends";
    host.style.cssText = "position:fixed;bottom:20px;right:20px;z-index:2147483000;";
    btn.style.cssText = [
      "all:initial",
      "cursor:pointer",
      "font:600 14px system-ui,sans-serif",
      "color:#fff",
      `background:${cfg.accentColor ?? "#3a86c8"}`,
      "padding:12px 18px",
      "border-radius:24px",
      "box-shadow:0 4px 14px rgba(0,0,0,.25)",
    ].join(";");
    shadow.appendChild(btn);
    document.body.appendChild(host);
  }

  btn.addEventListener("click", () => {
    btn.style.opacity = "0.5";
    onActivate();
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", main);
} else {
  main();
}
